import { api } from './client'

/**
 * Leitura do CloudHSM na conta do tenant (`src/consoleai/cloudhsm/routes.py`).
 *
 * So leitura: nao ha nada aqui que mude o cluster. Usuarios e chaves do HSM sao
 * administrados pelo admin do HSM com o `cloudhsm-cli`; o que o ConsoleAI mostra e
 * a trilha do que ele fez.
 *
 * A regra que este modulo preserva: **regiao nao lida nao e regiao vazia.** A rota
 * devolve `regioes_com_falha` (regiao -> motivo) ao lado de `clusters`, e a tela
 * precisa das duas coisas para nao afirmar "nenhum cluster" sobre uma conta que ela
 * nao conseguiu olhar. Por isso a normalizacao nunca descarta `regioes_com_falha`.
 */

export interface HsmCloudHsm {
  hsm_id: string
  estado: string
  mensagem_de_estado: string | null
  zona: string | null
  subnet_id: string | null
  eni_id: string | null
  ip: string | null
}

export interface RetencaoDeBackup {
  tipo: string
  valor: string
}

export interface ClusterCloudHsm {
  cluster_id: string
  regiao: string
  estado: string
  mensagem_de_estado: string | null
  tipo_de_hsm: string | null
  /** `FIPS` ou `NON_FIPS`. */
  modo: string | null
  vpc_id: string | null
  security_group: string | null
  subnets: Record<string, string>
  criado_em: string | null
  retencao_de_backup: RetencaoDeBackup | null
  hsms: HsmCloudHsm[]
}

export interface ListaDeClusters {
  clusters: ClusterCloudHsm[]
  regioes_consultadas: string[]
  regioes_lidas: string[]
  /** Regiao -> motivo (`leitura_nao_concedida` ou `aws_error:<Code>`). */
  regioes_com_falha: Record<string, string>
  regioes_do_tenant: string[]
}

export interface BackupCloudHsm {
  backup_id: string
  estado: string
  criado_em: string | null
  nunca_expira: boolean | null
  regiao_de_origem: string | null
  backup_de_origem: string | null
  copiado_em: string | null
  apagado_em: string | null
}

export interface ListaDeBackups {
  cluster_id: string
  regiao: string
  falha: string | null
  backups: BackupCloudHsm[]
}

export interface EventoDaTrilha {
  quando: string
  hsm: string | null
  /** Como o HSM escreveu — a tela mostra crua. */
  mensagem: string
}

export interface TrilhaDoHsm {
  cluster_id: string
  regiao: string
  log_group: string
  horas: number
  /** `false`: o HSM ainda nao escreveu trilha; `null`: nao deu para saber (ver `falha`). */
  log_group_existe: boolean | null
  eventos: EventoDaTrilha[]
  truncada: boolean
  janela_incompleta: boolean
  falha: string | null
}

const lista = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : [])
const textos = (v: unknown): string[] => lista<unknown>(v).filter((x): x is string => typeof x === 'string')

export async function listarClusters(tenantId: number, regioes?: string[]): Promise<ListaDeClusters> {
  const params = regioes && regioes.length > 0 ? { regioes: regioes.join(',') } : undefined
  const r = await api.get<ListaDeClusters>(`/v1/tenants/${tenantId}/cloudhsm/clusters`, { params })
  const d = r.data
  const falhas = d?.regioes_com_falha
  return {
    clusters: lista<ClusterCloudHsm>(d?.clusters).map((c) => ({
      ...c,
      subnets: c.subnets && typeof c.subnets === 'object' ? c.subnets : {},
      hsms: lista<HsmCloudHsm>(c.hsms),
    })),
    regioes_consultadas: textos(d?.regioes_consultadas),
    regioes_lidas: textos(d?.regioes_lidas),
    regioes_com_falha: falhas && typeof falhas === 'object' && !Array.isArray(falhas) ? falhas : {},
    regioes_do_tenant: textos(d?.regioes_do_tenant),
  }
}

export async function listarBackups(tenantId: number, clusterId: string, regiao: string): Promise<ListaDeBackups> {
  const r = await api.get<ListaDeBackups>(
    `/v1/tenants/${tenantId}/cloudhsm/clusters/${encodeURIComponent(clusterId)}/backups`,
    { params: { regiao } },
  )
  return {
    cluster_id: r.data?.cluster_id ?? clusterId,
    regiao: r.data?.regiao ?? regiao,
    falha: r.data?.falha ?? null,
    backups: lista<BackupCloudHsm>(r.data?.backups),
  }
}

export async function lerTrilha(
  tenantId: number, clusterId: string, regiao: string, horas: number, limite: number,
): Promise<TrilhaDoHsm> {
  const r = await api.get<TrilhaDoHsm>(
    `/v1/tenants/${tenantId}/cloudhsm/clusters/${encodeURIComponent(clusterId)}/trilha`,
    { params: { regiao, horas, limite } },
  )
  const d = r.data
  return {
    cluster_id: d?.cluster_id ?? clusterId,
    regiao: d?.regiao ?? regiao,
    log_group: d?.log_group ?? '',
    horas: d?.horas ?? horas,
    log_group_existe: typeof d?.log_group_existe === 'boolean' ? d.log_group_existe : null,
    eventos: lista<EventoDaTrilha>(d?.eventos),
    truncada: !!d?.truncada,
    janela_incompleta: !!d?.janela_incompleta,
    falha: d?.falha ?? null,
  }
}

/** Frase da tela para o motivo de uma regiao (ou leitura) que nao foi lida. */
export function explicarMotivo(motivo: string): string {
  if (motivo === 'leitura_nao_concedida') {
    return 'a permissão de leitura do CloudHSM ainda não chegou na role desta conta'
  }
  if (motivo.startsWith('aws_error:')) {
    return `a AWS recusou a leitura (${motivo.slice('aws_error:'.length)})`
  }
  return motivo
}
